// react
import { SyntheticEvent } from 'react';

// types
import { TraceabilityTransactions, TraceabilityType } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// ui
import { TableProps } from '../../ui/Table/Table';
import { TableBasicTraceabilityCurrencies } from '../../tables/basic/TableBasicTraceabilityCurrencies/TableBasicTraceabilityCurrencies';
import { Tabs } from '../../ui/Tabs/Tabs';

// props
export type DialogTraceabilityTab = 'financial' | 'transactions';

export interface DialogTraceabilityProps extends DialogActionsProps {
	isOpen: boolean;
	onClose: () => void;
	type?: TraceabilityType;
	traceability?: TraceabilityTransactions;
}

export const DialogTraceability = ({
	isOpen,
	onClose,
	type = 'TRANSACTIONS',
	traceability,
	...props
}: DialogTraceabilityProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogTab = searchParams.get('dialogTab') as DialogTraceabilityTab | null;

	// handlers
	const handleChangeTab = (e: SyntheticEvent, value: DialogTraceabilityTab) => {
		searchParams.set('dialogTab', value);
		setSearchParams(searchParams);
	};

	const handleClose = () => {
		searchParams.delete('dialogTab');
		setSearchParams(searchParams);
		onClose();
	};

	// vars
	const tabValue: DialogTraceabilityTab =
		paramDialogTab || (type === 'FINANCIAL' ? 'financial' : 'transactions');
	const tabs = [
		{
			label: t('transactionsEvidenced'),
			value: 'transactions',
		},
		{
			label: t('financialValueEvidenced'),
			value: 'financial',
		},
	];

	return (
		<DialogActions
			actions={[
				{
					children: t('done'),
					onClick: handleClose,
					variant: 'contained',
				},
			]}
			hasClose={true}
			isOpen={isOpen}
			maxWidth={700}
			onClose={handleClose}
			title={{
				children: t('traceability'),
				variant: 'h3',
			}}
			{...props}>
			<TabContext value={tabValue}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Tabs onChange={handleChangeTab} tabs={tabs} value={tabValue} />
					</Grid>
					<Grid item xs={12}>
						<TabPanel value="transactions">
							<Grid container spacing={2}>
								{traceability && (
									<>
										<Grid item xs={12}>
											<Typography fontWeight={600}>{t('currencies')}</Typography>
										</Grid>
										<Grid item xs={12}>
											<TableBasicTraceabilityCurrencies
												data={traceability?.documentation?.currencies as TableProps['data']}
												traceability={traceability}
												type="TRANSACTIONS"
											/>
										</Grid>
									</>
								)}
								<Grid item xs={12}>
									<Typography fontWeight={600}>
										{t('dialogTraceability.transactions.what.title')}
									</Typography>
									<Typography>{t('dialogTraceability.transactions.what.content')}</Typography>
								</Grid>
								<Grid item xs={12}>
									<Alert severity="info">
										<AlertTitle>{t('important')}</AlertTitle>
										{t('alert.traceabilityFull')}
									</Alert>
								</Grid>
							</Grid>
						</TabPanel>
						<TabPanel value="financial">
							<Grid container spacing={2}>
								{traceability && (
									<>
										<Grid item xs={12}>
											<Typography fontWeight={600}>{t('currencies')}</Typography>
										</Grid>
										<Grid item xs={12}>
											<TableBasicTraceabilityCurrencies
												data={traceability?.financial?.currencies as TableProps['data']}
												traceability={traceability}
												type="FINANCIAL"
											/>
										</Grid>
									</>
								)}
								<Grid item xs={12}>
									<Typography fontWeight={600}>
										{t('dialogTraceability.financial.what.title')}
									</Typography>
									<Typography>{t('dialogTraceability.financial.what.content')}</Typography>
								</Grid>
								<Grid item xs={12}>
									<Alert severity="info">
										<AlertTitle>{t('important')}</AlertTitle>
										{t('alert.traceabilityFull')}
									</Alert>
								</Grid>
							</Grid>
						</TabPanel>
					</Grid>
				</Grid>
			</TabContext>
		</DialogActions>
	);
};
